import React, { useState, memo, useEffect } from 'react';
import { Box } from '@mui/material';
import { convertCurrencyToDecimal, convertDecimalToCurrency, handleCurrencyChange } from './utils';
import { TableCellVariant } from './types';

interface CurrencyInputCellProps {
  value: number | null | string;
  row: number;
  col: number;
  onChange: (value: number | string | null, row: number, col: number) => void;
  onPaste?: (event: React.ClipboardEvent<HTMLInputElement>, row: number, col: number) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  variant: TableCellVariant;
  isSelected?: boolean;
  isCopied?: boolean;
  isPasted?: boolean;
}

const cellStyles = {
  backgroundColor: 'white',
  fontSize: '14px',
  lineHeight: '20px',
  fontWeight: 500,
  color: '#162936',
};

const currencyBlackCellStyles = {
  backgroundColor: 'white',
  fontSize: '14px',
  lineHeight: '20px',
  fontWeight: 500,
  color: '#1B1C17',
};

const resolveVariantStyles = (variant: TableCellVariant): any => {
  switch (variant) {
    case 'secondary-black':
      return currencyBlackCellStyles;
    default:
      return cellStyles;
  }
};
const CurrencyInputCell = memo(
  ({
    value: initialValue,
    row,
    col,
    onChange,
    onPaste,
    onFocus,
    onBlur,
    variant,
    isSelected,
    isCopied,
    isPasted,
  }: CurrencyInputCellProps) => {
    const [formattedValue, setFormattedValue] = useState('');

    useEffect(() => {
      setFormattedValue(
        initialValue === null || initialValue === undefined || initialValue === ''
          ? ''
          : convertDecimalToCurrency(Number(initialValue)),
      );
    }, [initialValue]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;

      if (formattedValue === '-$' && inputValue === '-') {
        setFormattedValue('');
        onChange(null, row, col);
        return;
      }

      if (inputValue === '-') {
        setFormattedValue('-$');
        onChange(null, row, col);
        return;
      }

      if (inputValue === '' || inputValue === '$') {
        setFormattedValue('');
        onChange(null, row, col);
        return;
      }

      const newActualValue = convertCurrencyToDecimal(inputValue);
      handleCurrencyChange(e, setFormattedValue);
      onChange(newActualValue, row, col);
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
      if (onPaste) {
        onPaste(e, row, col);
      }
    };

    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <input
          style={{
            width: '100%',
            height: '100%',
            border: 'none',
            outline: 'none',
            textAlign: 'right',
            paddingRight: '8px',
            ...cellStyles,
            ...(isSelected && { backgroundColor: '#2F736E2F', color: '#1B1C17' }),
            ...(isCopied && {
              outline: '2px dashed #2F736E',
              outlineOffset: '-3px',
              backgroundColor: '#2F736E2F',
            }),
            ...(isPasted && { backgroundColor: '#2F736E2F', color: '#1B1C17' }),
            ...resolveVariantStyles(variant),
          }}
          type="text"
          value={formattedValue}
          onChange={handleChange}
          onPaste={handlePaste}
          onFocus={onFocus}
          onBlur={onBlur}
        />
      </Box>
    );
  },
);

CurrencyInputCell.displayName = 'CurrencyInputCell';

export default CurrencyInputCell;
